import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Menu, MenuProps, Modal } from 'antd';
import { useLocation, useNavigate } from 'react-router';
import getActiveTab from '../helpers/getActiveTab';
import logo from '../assets/images/shmlogo-blue-2.png';
import logo2 from '../assets/images/shmlogo-blue-2 2.png';
import { SidebarProps } from '../interfaces/sidebar-interfaces';
import { allAnetarKomisioni } from '../constants/all-anetar-komisioni';
import {
  sidebarMenuOptions,
  SidebarMenuOptionsProps,
} from '../constants/SidebarMenuOptions';
import { ROLES } from '../constants/enums/roles';
import { useSelector } from 'react-redux';
import { UserStateProps } from '../interfaces/modules.interface';
import { useRoleGetter } from '../helpers/getUserInfo';
import { MenuItemType } from 'antd/es/menu/hooks/useItems';
import SidebarLayout from './SidebarLayout';
import { style } from './styles';
import { RootState } from '../redux/store';
import { useRedirect } from '../hooks/use-redirect';
import { useDeviceCollapse } from '../hooks/use-device-collapse';
import {
  getActiveUser,
  getAllValidPaths,
} from '../components/common/common-functions/common-functions';
import { useAuth } from '../hooks/use-auth';
import BottomBar from './bottom-bar';
import routesWithPermissions from '../routes/routesWithPermissions';
import { useSidebarItems } from '../hooks/use-sidebar-items';
import { AuthProvider } from '../utilities/enums';
import ModalTitle from '../components/common/common-componnets/ModalTitle';
import TedhenaPersonale from '../components/SettingsModal/TedhenaPersonale';
import { CiSettings } from 'react-icons/ci';
const { Sider } = Layout;

export const Sidebar = ({ children }: SidebarProps) => {
  const { admin, pjesmarres, specialistFv } = ROLES;
  const { pathname } = useLocation();
  const userRole = useRoleGetter();
  const { sidebarItems } = useSidebarItems();

  const selectorRole = useSelector((state: UserStateProps) => state.activeUser);
  const navigate = useNavigate();
  const { data: loggedInUser, isFetching } = useAuth();

  const { collapsed, setCollapsed, isMobile } = useDeviceCollapse();
  const [showPersonalDataModal, setShowPersonalDataModal] = useState(false);
  const redirectUrl = useRedirect(setCollapsed);

  const { showVerifyFA, showSuggestTfa } = useSelector(
    (state: RootState) => state.verifyFA,
  );

  const role = useMemo(
    () => (selectorRole?.length > 0 ? selectorRole : getActiveUser()),
    [selectorRole],
  );

  const reorderItems = useMemo(() => {
    const sidebarOptions = sidebarMenuOptions(userRole, showVerifyFA);
    if (
      loggedInUser &&
      allAnetarKomisioni.includes(role ?? loggedInUser.rolet[0])
    ) {
      const foundIdx = sidebarOptions.findIndex(el => el.key === '/konkurrime');
      sidebarOptions.push(...sidebarOptions.splice(0, foundIdx));
      return sidebarOptions;
    }
    return sidebarOptions;
  }, [userRole, showVerifyFA, role, loggedInUser, pathname]);

  const defaultOpenKeys = useMemo(() => {
    if (userRole === admin) {
      return ['#'];
    } else if (userRole === specialistFv || userRole === pjesmarres) {
      return ['*', 'trajnime'];
    }

    return ['/lende-akademike'];
  }, [userRole]);

  useEffect(() => {
    if (isFetching || !loggedInUser) {
      return;
    }

    if (
      loggedInUser.firstLogIn &&
      loggedInUser?.authProvider !== AuthProvider.MICROSOFT
    ) {
      navigate('/ndryshoFjalekalimin');
      return;
    }
    const loggedInUserRole = loggedInUser.rolet[0];
    const currentRole = role ?? loggedInUserRole;

    if (currentRole === pjesmarres && loggedInUser.isUpdated === false) {
      setShowPersonalDataModal(true);
      return;
    }

    if (loggedInUser.rolet.length > 1 && !role) {
      setCollapsed(true);
      return;
    }

    const userRoles: SidebarMenuOptionsProps[] = [];
    for (let index = 0; index < reorderItems.length; index++) {
      const element = reorderItems[index];
      if (element.roles.includes(currentRole)) {
        userRoles.push(element);
      }
    }

    const sidebarPaths = getAllValidPaths(userRoles);

    const isAllowedPath = Object.entries(routesWithPermissions).some(
      ([routePath, allowedRoles]) => {
        const routePattern = routePath.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${routePattern}$`);
        return regex.test(pathname) && allowedRoles.includes(currentRole);
      },
    );

    const isOnSidebarPath = sidebarPaths.some(path =>
      pathname.startsWith(path),
    );

    if (pathname === '/' || (!isAllowedPath && !isOnSidebarPath)) {
      redirectUrl();
    }
  }, [
    role,
    loggedInUser,
    isFetching,
    showSuggestTfa,
    showVerifyFA,
    pathname,
    reorderItems.length,
    redirectUrl,
  ]);

  const excludePathnames = [
    '/login',
    '/redirect',
    '/forgot-password',
    'logout-success',
  ];

  if (excludePathnames.includes(pathname)) {
    return children;
  }

  const onClick: MenuProps['onClick'] = () => {
    getActiveTab({
      pathname,
    });
  };

  if (isMobile) {
    return (
      <Layout style={{ backgroundColor: '#003666', minHeight: '100vh' }}>
        <SidebarLayout {...{ children, collapsed: true, isMobile: true }} />
        <BottomBar items={sidebarItems()} />
        {showPersonalDataModal && (
          <Modal
            open={showPersonalDataModal}
            footer={null}
            closable={false}
            maskClosable={false}
            centered
            width={700}
            title={
              <ModalTitle
                title="Të dhënat personale"
                icon={<CiSettings className="h-8 w-8" />}
              />
            }
          >
            <TedhenaPersonale
              onSuccess={() => setShowPersonalDataModal(false)}
            />
          </Modal>
        )}
      </Layout>
    );
  }

  return (
    <Layout style={{ backgroundColor: '#003666', minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        style={style}
        id="sidebar1"
      >
        <div className="h-8" />
        <div className="flex justify-center bg-white">
          {collapsed ? (
            <div className="w-14 py-1" style={{ height: '62px' }}>
              <img className="object-contain" src={logo2} />
            </div>
          ) : (
            <div className="h-15">
              <img className="z-[100] object-fill py-2" src={logo} />
            </div>
          )}
        </div>

        <Menu
          selectedKeys={[pathname]}
          theme="dark"
          defaultOpenKeys={defaultOpenKeys}
          defaultSelectedKeys={['trajnime']}
          style={{
            backgroundColor: '#003666',
            paddingTop: '36px',
            color: '#FFFFFF',
          }}
          mode="inline"
          items={sidebarItems() as unknown as MenuItemType[]}
          onClick={e => {
            onClick(e);
            navigate(e.key);
          }}
        />
      </Sider>
      <SidebarLayout {...{ children, collapsed, isMobile: false }} />
      {showPersonalDataModal && (
        <Modal
          open={showPersonalDataModal}
          footer={null}
          closable={false}
          maskClosable={false}
          centered
          width={700}
          title={
            <ModalTitle
              title="Të dhënat personale"
              icon={<CiSettings className="h-8 w-8" />}
            />
          }
        >
          <TedhenaPersonale onSuccess={() => setShowPersonalDataModal(false)} />
        </Modal>
      )}
    </Layout>
  );
};
